import { z } from 'zod'
import { CreditNoteStatus } from '@/features/credit-notes/constants'

export const CreditNoteChargeSchema = z.object({
  id: z.number().int().optional(),
  description: z.string().min(1),
  kWh: z.number().nonnegative().default(0),
  tariffPerKWh: z.number().nonnegative().default(0),
  subtotalInFractionalCurrency:z.number().optional()
})

export const AssetSummaryForCreditNoteSchema = z.object({
  projectCode: z.number().int(),
  projectName: z.string(),
  assetCode: z.string(),
  customerExternalIdentifier: z.string(),

  contract: z.object({
    id: z.number().int(),

    contractFundEntity: z.object({
      id: z.number().int(),
      name: z.string(),
      registrationNumber: z.string(),
      address: z.string(),
      vatNumber: z.string(),
      logoUrl: z.string().nullable(),
      accountName: z.string(),
      bank: z.string(),
      accountNumber: z.string(),
      branchCode: z.string()
    })
  })
})

export const CreditNotePartySchema = z.object({
  name: z.string(),
  logoUrl: z.string().nullable(),
  address: z.string().optional().default(''),
  vatNumber: z.string().optional().default('')
})

export const customerSchema = z.object({
  name: z.string(),
  addressLineOne: z.string().nullable(),
  addressLineTwo: z.string().nullable(),
  suburb: z.string().nullable(),
  city: z.string().nullable(),
  region: z.string().nullable(),
  country: z.string().nullable(),
  vatNumber: z.string().nullable()
})

export const contractFundEntity = z.object({
  name: z.string(),
  registrationNumber: z.string(),
  address: z.string(),
  vatNumber: z.string(),
  logoUrl: z.string().nullable(),
  accountName: z.string(),
  bank: z.string(),
  accountNumber: z.string(),
  branchCode: z.string()
})

export const CreditNoteSchema = z.object({
  id: z.number().int().optional(),
  backendId: z.number().optional(),
  creditNoteNumber: z.string().optional(), // e.g. "CN-0007"
  date: z.coerce.date().default(() => new Date()),
  creditNoteDateFormat: z.string().default('d MMMM yyyy'),
  status: z.nativeEnum(CreditNoteStatus).default(CreditNoteStatus.Draft),

  projectCode: z.number().int(),
  assetCode: z.string().min(1),
  projectName: z.string(),

  owner: contractFundEntity,
  customer: customerSchema,

  customerAccountCode: z.string().optional(),
  notes: z.string().optional().default(''),

  charges: z.array(CreditNoteChargeSchema).default([]),

  subTotalInFractionalCurrency: z.number().nonnegative(),
  vatPercentage: z.number().min(0).max(100),
  vatInFractionalCurrency: z.number().nonnegative(),
  totalInFractionalCurrency: z.number().nonnegative()
})

export const PaginatedCreditNoteSchema = z.object({
  records: z.array(CreditNoteSchema),
  total: z.number().int(),
  page: z.number().int(),
  pageSize: z.number().int()
})

export const CreateCreditNoteItemSchema = z.object({
  description: z.string().min(1),
  tariffInCents: z.number().nonnegative(),
  quantityInKWh: z.number().nonnegative()
})

export const CreateCreditNoteRequestSchema = z.object({
  assetCode: z.string().min(1),
  notes: z.string().nullable().default(''),
  items: z.array(CreateCreditNoteItemSchema).min(1)
})

export const CreateCreditNoteResponseSchema = z.object({
  id: z.number().int(),
  creditNoteReference: z.string(),
  date: z.string(),
  status: z.string(),
  totalAmountInCents: z.number(),
  vatPercentage: z.number(),
  vatAmountInCents: z.number(),
  notes: z.string().optional().nullable(),
  creditNoteItems: z.array(CreateCreditNoteItemSchema.extend({ id: z.number().int() }))
})

export const UpdateCreditNoteItemSchema = CreateCreditNoteItemSchema.extend({
  id: z.number().int()
})

export const UpdateCreditNoteRequestSchema = z.object({
  id: z.number().int(),
  notes: z.string().optional().nullable(),
  creditNoteItems: z.array(UpdateCreditNoteItemSchema).min(1)
})


export const AssetVatSchema = z.object({
  contractFundEntityId: z.number().int(),
  ratePercentage: z.number()
})

// Response shape is the same as create
export const UpdateCreditNoteResponseSchema = CreateCreditNoteResponseSchema
export const GetCreditNoteResponseSchema = CreateCreditNoteResponseSchema

export type AssetVat = z.infer<typeof AssetVatSchema>
export type UpdateCreditNoteRequest = z.infer<typeof UpdateCreditNoteRequestSchema>
export type UpdateCreditNoteResponse = z.infer<typeof UpdateCreditNoteResponseSchema>
export type GetCreditNoteResponse = z.infer<typeof GetCreditNoteResponseSchema>

export type CreditNote = z.infer<typeof CreditNoteSchema>
export type CreditNoteCharge = z.infer<typeof CreditNoteChargeSchema>
export type PaginatedCreditNotes = z.infer<typeof PaginatedCreditNoteSchema>
export type CreateCreditNoteRequest = z.infer<typeof CreateCreditNoteRequestSchema>
export type CreateCreditNoteResponse = z.infer<typeof CreateCreditNoteResponseSchema>
export type GetAssetDetailsResponse = z.infer<typeof AssetSummaryForCreditNoteSchema>
export type CreditNoteCustomer = z.infer<typeof customerSchema>